import React, { useEffect, useState } from 'react'
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native'

import { faChevronDown } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome'
import { Separator } from '../../../../components/Separator'
import { FormatPond } from '../../../../context/FreshWater/types'
import {
  corporateColor,
  grayBorders,
  successColor,
  textColor,
  white,
  disabledGray,
} from '../../../../theme/colors'
import { MortalityItem } from '../../../../types'
import { useTranslation } from 'react-i18next'

interface Props {
  item: FormatPond
  isLastItem: boolean
  typesOfMoratality: MortalityItem
  name: string
  mortalityValues: { [key: string]: number }
  date: string
  showTypesOfMoratlity: (values: { levelThreeId: number; levelThreeName: string }) => void
  getValues: (value: { [key: string]: number }) => void
}

const PondCageOptionComponent = ({
  item,
  isLastItem,
  showTypesOfMoratlity,
  typesOfMoratality,
  mortalityValues,
  getValues,
  date,
  name,
}: Props) => {
  const { t } = useTranslation()
  const [values, setValues] = useState<{ [key: string]: number }>({})

  const typesOfMortalityList = Object.values(typesOfMoratality)

  const typesOfMortalityByPond = typesOfMortalityList.filter(
    (mortality) => mortality.levelThreeId === item.id && mortality.date === date,
  )

  useEffect(() => {
    setValues(() => {
      const keysByPonds = Object.keys(mortalityValues).filter(
        (key) => key.includes(name) && key.includes(date),
      )
      return Object.fromEntries(keysByPonds.map((key) => [[key], mortalityValues[key]]))
    })
  }, [date])

  useEffect(() => {
    if (Object.values(values).length > 0) getValues(values)
  }, [values])

  const handleInputChange = ({ value, name }: { value: string; name: string }) => {
    setValues((oldState) => ({
      ...oldState,
      [name]: value === '' ? 0 : +value,
    }))
  }

  return (
    <>
      <View style={[styles.container, isLastItem && { marginBottom: 100 }]}>
        <View style={[{ flex: 1 }, typesOfMortalityByPond.length > 0 && { flex: 0.8 }]}>
          <Text style={styles.stateText}>{item.name}</Text>
        </View>

        {typesOfMortalityByPond.length === 0 && (
          <TouchableOpacity
            onPress={() =>
              showTypesOfMoratlity({ levelThreeId: item.id, levelThreeName: item.name })
            }
            style={styles.buttonTypes}
          >
            <Text style={{ color: textColor }}>{t('TIPOS')}</Text>
            <FontAwesomeIcon icon={faChevronDown} size={20} color={corporateColor} />
          </TouchableOpacity>
        )}

        {typesOfMortalityByPond.length > 0 && (
          <View style={{ flex: 2 }}>
            <View style={{ flexDirection: 'row' }}>
              <TouchableOpacity
                onPress={() =>
                  showTypesOfMoratlity({ levelThreeId: item.id, levelThreeName: item.name })
                }
                style={[styles.buttonTypes, { flex: 1 }]}
              >
                <Text style={{ color: textColor }}>TIPOS</Text>
                <FontAwesomeIcon icon={faChevronDown} size={20} color={corporateColor} />
              </TouchableOpacity>
              <View style={[styles.buttonTypes, styles.disabledInput]}>
                <Text style={{ fontSize: 15, fontWeight: 'bold', color: textColor }}>
                  {Object.values(values).reduce((acc, value) => (acc = acc + value), 0)}
                </Text>
              </View>
            </View>

            {typesOfMortalityByPond.map((item) => (
              <View style={{ flexDirection: 'row' }} key={item.mortalityTypeId}>
                <View style={[styles.buttonTypes, styles.smallButton]}>
                  <Text style={styles.mortalityName}>{item.mortalityName}</Text>
                </View>
                <TextInput
                  keyboardType='numeric'
                  style={[styles.buttonTypes, { flex: 1 }]}
                  placeholder='0'
                  value={values[
                    `${item.levelThreeId}-${item.date}-${item.mortalityTypeId}`
                  ]?.toString()}
                  onChangeText={(value) => {
                    handleInputChange({
                      value,
                      name: `${item.levelThreeId}-${item.date}-${item.mortalityTypeId}`,
                    })
                  }}
                />
              </View>
            ))}
          </View>
        )}
      </View>
      {!isLastItem && <Separator />}
    </>
  )
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  stateText: {
    color: corporateColor,
    fontWeight: 'bold',
  },
  buttonTypes: {
    flex: 2,
    padding: 15,
    borderWidth: 1,
    borderRadius: 10,
    borderColor: grayBorders,
    margin: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
    color: textColor,
  },

  smallButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: successColor,
  },

  mortalityName: {
    color: white,
    fontWeight: 'bold',
    fontSize: 15,
    textAlign: 'center',
  },

  disabledInput: {
    flex: 1,
    backgroundColor: disabledGray,
  },
})

export const PondCageOption = React.memo(PondCageOptionComponent, (prevProps, nextProps) => {
  return (
    prevProps.item.id === nextProps.item.id &&
    prevProps.isLastItem === nextProps.isLastItem &&
    prevProps.date === nextProps.date &&
    prevProps.name === nextProps.name &&
    JSON.stringify(prevProps.typesOfMoratality) === JSON.stringify(nextProps.typesOfMoratality) &&
    JSON.stringify(prevProps.mortalityValues) === JSON.stringify(nextProps.mortalityValues)
  )
})
